import React, { useState } from 'react';
import { Typography, Button } from '@navi/web-ui/lib/primitives';
import { ExperimentData } from './interfaces';
import { sampleExperimentFile } from './constants';
import { downloadContent } from '@cp/src/utils/commonUtils';
import FileUploadContainerV2 from '@cp/src/components/UploadComponent/FileUploadContainerV2';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { setFailureReport } from '../../reducers/FieldAllocationSlice';
import { downloadDocumentFromS3WithoughtPreview } from '@cp/src/utils/DocumentDownloadFromS3';
import { DownloadIcon } from '@navi/web-ui/lib/icons';
import RequiredIcon from '@navi/web-ui/lib/icons/RequiredIcon';
import ExistingFileDownloadComponent from '../../../../components/UploadComponent/ExistingFileDownloadComponent';

interface UploadFileDrawerProps {
  handleFileRemove: () => void;
  onFileSelect: (file: File | null) => void;
  userRemovedFile: boolean;
}

const UploadFileDrawer: React.FC<UploadFileDrawerProps> = ({
  handleFileRemove,
  onFileSelect,
  userRemovedFile
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [error, setError] = useState<{ message: string | boolean; fileName: string | null }>({
    message: '',
    fileName: null
  });
  const [showLoadingComponent, setShowLoadingComponent] = useState(false);
  const experimentData = useSelector((state: RootState) => state?.fieldAllocation?.experimentData);
  const { name, details, originalFileUri, originalFileName } = experimentData || {};
  const dispatch = useDispatch();
  const failureReport = useSelector((state: RootState) => state?.fieldAllocation?.failureReport);

  const handleDownloadSampleFileClick = (content: string[], name: string, type: string) => {
    downloadContent(content, name, type);
  };

  const downloadFailureReport = (e: React.MouseEvent, fileName: string, failureReport: string) => {
    e.preventDefault();
    downloadDocumentFromS3WithoughtPreview(failureReport, fileName, `File download failed`);
  };

  if (!experimentData) {
    return <div className="p-6">No experiment data available</div>;
  }

  return (
    <div className="px-4 pt-4">
      <div className="mb-4">
        <Typography variant="h3" className="text-[16px] font-semibold text-[--grayscale-1] mb-2">
          {name}
        </Typography>
      </div>

      <div className="mb-2">
        <Typography variant="h6" className="text-[12px] text-[--grayscale-1] leading-relaxed">
          {details}
        </Typography>
      </div>

      <div>
        <div className="flex items-center">
          <div className="text-[--grayscale-1] text-[14px] mt-[16px]">Upload LANs</div>
          <RequiredIcon className="mt-3 ml-1" />
        </div>
        {originalFileUri && !userRemovedFile ? (
          <ExistingFileDownloadComponent
            fileUrl={originalFileUri}
            fileName={originalFileName || 'experiment_data.csv'}
            onRemoveFile={() => {
              handleFileRemove();
            }}
            onDownloadFile={() => {
              if (originalFileUri) {
                downloadDocumentFromS3WithoughtPreview(
                  originalFileUri,
                  originalFileName || 'experiment_data.csv',
                  `File download failed`
                );
              }
            }}
          />
        ) : (
          <FileUploadContainerV2
            error={error}
            file={uploadedFile}
            setFile={file => {
              const selectedFile = typeof file === 'function' ? file(uploadedFile) : file;
              setUploadedFile(selectedFile);
              onFileSelect(selectedFile);
              if (!selectedFile) {
                dispatch(setFailureReport(null));
              }
            }}
            setError={setError}
            showLoadingComponent={showLoadingComponent}
            setShowLoadingComponent={setShowLoadingComponent}
            handleDownloadClick={() =>
              handleDownloadSampleFileClick([sampleExperimentFile], 'sample_experiment.csv', 'csv')
            }
            styles={{}}
            supportedFile=".csv"
            isSampleFileAvailable
            statesContainerClassName="flex items-center !ml-0"
            errorContainerClassName="flex items-center !ml-0"
            messageClassName="!ml-0 text-[13px]"
          />
        )}

        {failureReport && (
          <Button
            onClick={e => downloadFailureReport(e, 'Failure Report', failureReport)}
            variant="text"
            className="flex justify-start mt-2"
            startAdornment={
              <DownloadIcon width={16} height={16} color="var(--navi-color-blue-base)" />
            }
          >
            <Typography variant="h4" color="var(--navi-color-blue-base)">
              Failure Report
            </Typography>
          </Button>
        )}
      </div>
    </div>
  );
};

export default UploadFileDrawer;
